import React from 'react';
import { BarChart, Bar, XAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1e293b]/90 backdrop-blur-md border border-white/10 p-3 rounded-lg shadow-xl">
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-1">{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-gray-300">{entry.name}:</span>
                        <span className="text-white font-bold font-mono">{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const IndividualPlayerStats = React.memo(({ playerStats }) => {
    if (!playerStats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-400 font-medium animate-pulse">Loading player stats...</div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {playerStats.map(stats => (
                <div key={stats.name} className="bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl flex flex-col h-full hover:border-amber-500/20 transition-colors duration-300">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="font-bold text-lg uppercase tracking-wider flex items-center gap-3" style={{ color: stats.color }}>
                            <span className="w-3 h-3 rounded-full shadow-[0_0_10px_currentColor]" style={{ backgroundColor: stats.color }}></span>
                            {stats.name}
                        </h3>
                        <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-bold text-gray-400">
                            STATS
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-1">Avg. Bid</p>
                            <p className="font-black text-2xl text-white">{stats.averageBid}</p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-1">Avg. Won</p>
                            <p className="font-black text-2xl text-white">{stats.averageTricksWon}</p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-1">Avg. Bags</p>
                            <p className="font-black text-2xl text-amber-400">{stats.averageBags}</p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-1">Risk Level</p>
                            <div className="flex items-baseline gap-2">
                                <p className="font-black text-2xl text-white">{stats.riskLevel}%</p>
                                <span className={`text-xs font-bold ${stats.riskLevel >= 75 ? 'text-red-400' : stats.riskLevel >= 50 ? 'text-yellow-400' : 'text-green-400'}`}>
                                    {stats.riskLevel >= 75 ? 'High' : stats.riskLevel >= 50 ? 'Moderate' : 'Low'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Advanced Stats Row */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="bg-black/20 rounded-2xl p-3 border border-white/5 text-center">
                            <p className="text-gray-500 text-[9px] font-bold uppercase tracking-wider mb-1">Best Hand</p>
                            <p className="font-black text-lg text-emerald-400">
                                {stats.bestHand?.score > -Infinity ? `+${stats.bestHand.score}` : '-'}
                            </p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-3 border border-white/5 text-center">
                            <p className="text-gray-500 text-[9px] font-bold uppercase tracking-wider mb-1">Worst Hand</p>
                            <p className="font-black text-lg text-red-400">
                                {stats.worstHand?.score < Infinity ? stats.worstHand.score : '-'}
                            </p>
                        </div>
                        <div className="bg-black/20 rounded-2xl p-3 border border-white/5 text-center">
                            <p className="text-gray-500 text-[9px] font-bold uppercase tracking-wider mb-1">Max Streak</p>
                            <p className="font-black text-lg text-blue-400">
                                {stats.maxStreak} <span className="text-[10px] text-gray-500 font-normal">hands</span>
                            </p>
                        </div>
                    </div>

                    <div className="bg-black/20 rounded-2xl p-4 border border-white/5 mb-6">
                        <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-4 text-center">Bids vs Won per Hand</p>
                        <div className="w-full h-48 md:h-64">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={20}>
                                <BarChart data={stats.bidsVsWonData} barGap={2}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                    <XAxis dataKey="hand" stroke="#64748b" tick={{ fontSize: 9, fontWeight: 'bold' }} axisLine={false} tickLine={false} dy={5} interval={0} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                    <Bar dataKey="Bids" fill="#818cf8" radius={[2, 2, 0, 0]} maxBarSize={12} />
                                    <Bar dataKey="Won" fill="#34d399" radius={[2, 2, 0, 0]} maxBarSize={12} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Achievements Section */}
                    {stats.achievements && stats.achievements.length > 0 && (
                        <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
                            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                                <span>🏆</span> Achievements
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {stats.achievements.map(achievement => (
                                    <div key={achievement.id} className="bg-white/5 rounded-xl p-2 flex items-center gap-3 border border-white/5">
                                        <div className="text-2xl">{achievement.icon}</div>
                                        <div>
                                            <p className={`text-xs font-bold ${achievement.color}`}>{achievement.title}</p>
                                            <p className="text-[9px] text-gray-400 leading-tight">{achievement.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
});

export default IndividualPlayerStats;
